/**
 * Content Studio API service.
 * Talks only to /content-studio/* endpoints. The api interceptor unwraps the
 * backend's { success, data } envelope, so res.data holds the payload and
 * res.success / res.message carry status.
 */
import { apiClient } from './api';

const BASE = '/content-studio';

const contentStudioService = {
  // ── KeyCrew OS directory (read-only) ──────────────────────────────────────
  async listKeycrewClients() {
    const res = await apiClient.get(`${BASE}/keycrew-clients`);
    return res.data; // { clients, total }
  },

  // ── Content Studio clients ────────────────────────────────────────────────
  async listClients() {
    const res = await apiClient.get(`${BASE}/clients`);
    return res.data; // { clients, total }
  },
  async getClient(id) {
    const res = await apiClient.get(`${BASE}/clients/${id}`);
    return res.data;
  },
  async createClient(data) {
    const res = await apiClient.post(`${BASE}/clients`, data);
    return res.data;
  },
  async updateClient(id, data) {
    const res = await apiClient.put(`${BASE}/clients/${id}`, data);
    return res.data;
  },
  async deleteClient(id) {
    const res = await apiClient.delete(`${BASE}/clients/${id}`);
    return res;
  },

  // ── Client Images ─────────────────────────────────────────────────────────
  async listClientImages(clientId) {
    const res = await apiClient.get(`${BASE}/clients/${clientId}/images`);
    return res.data; // { images, total }
  },
  async uploadClientImage(clientId, file) {
    const form = new FormData();
    form.append('file', file);
    const res = await apiClient.post(`${BASE}/clients/${clientId}/images`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  },
  async deleteClientImage(imageId) {
    const res = await apiClient.delete(`${BASE}/client-images/${imageId}`);
    return res;
  },

  // ── Submissions ───────────────────────────────────────────────────────────
  async listSubmissions(clientId) {
    const res = await apiClient.get(`${BASE}/submissions`, {
      params: clientId ? { client_id: clientId } : {},
    });
    return res.data; // { submissions, total }
  },
  async getSubmission(id) {
    const res = await apiClient.get(`${BASE}/submissions/${id}`);
    return res.data; // { submission, assets }
  },
  async createSubmission(data) {
    const res = await apiClient.post(`${BASE}/submissions`, data);
    return res.data;
  },
  async updateSubmission(id, data) {
    const res = await apiClient.put(`${BASE}/submissions/${id}`, data);
    return res.data;
  },
  async deleteSubmission(id) {
    const res = await apiClient.delete(`${BASE}/submissions/${id}`);
    return res;
  },

  // ── Submission assets ─────────────────────────────────────────────────────
  async addAsset(submissionId, data) {
    const res = await apiClient.post(`${BASE}/submissions/${submissionId}/assets`, data);
    return res.data;
  },
  async deleteAsset(assetId) {
    const res = await apiClient.delete(`${BASE}/assets/${assetId}`);
    return res;
  },

  // ── Articles (read-only until generation phase) ───────────────────────────
  async listArticles(params = {}) {
    const res = await apiClient.get(`${BASE}/articles`, { params });
    return res.data; // { articles, total }
  },
  async createArticle(data) {
    const res = await apiClient.post(`${BASE}/articles`, data);
    return res.data; // the created article
  },
  async deleteArticle(id) {
    const res = await apiClient.delete(`${BASE}/articles/${id}`);
    return res;
  },

  // ── Generation (reuses the editorial engine; writes to content_studio_article)
  async generate(submissionId, batch) {
    // Returns immediately (202); the 2–5 min work runs in the background.
    const res = await apiClient.post(`${BASE}/submissions/${submissionId}/generate/${batch}`, {});
    return res.data; // { started, batch }
  },

  // ── Interview-shaped rows for the Content Studio Interviews table ──────────
  async listInterviewRows(params = {}) {
    const res = await apiClient.get(`${BASE}/interviews`, { params });
    return res.data; // { interviews, total }
  },

  // ── Article workflow (manual internal review only) ───────────────────────
  async updateArticleWorkflow(id, workflow) {
    const res = await apiClient.patch(`${BASE}/articles/${id}/workflow`, workflow);
    return res.data;
  },

  // ── Article edit (title / body / internal_review / destination_names) ─────
  async updateArticle(id, data) {
    const res = await apiClient.patch(`${BASE}/articles/${id}`, data);
    return res.data;
  },

  // Account Manager Assistant (Beta) — returns a proposed draft (non-destructive).
  // `instructions` are optional one-off AM directions for this run.
  async assistArticle(id, instructions = '') {
    // The assistant rewrites the whole article — needs far more than the 30s default timeout.
    const res = await apiClient.post(`${BASE}/articles/${id}/assist`, { instructions }, { timeout: 300000 });
    return res.data;
  },

  // Workflow-only: move a pushed article back to In progress (does NOT unpublish WP).
  async moveToInProgress(id) {
    const res = await apiClient.post(`${BASE}/articles/${id}/move-to-in-progress`);
    return res.data;
  },

  // ── Client "Request edits" comments ───────────────────────────────────────
  // Public (preview page): submit + list, scoped by preview token.
  async addPreviewComment(token, payload) {
    const res = await apiClient.post(`${BASE}/previews/${token}/comments`, payload);
    return res.data;
  },
  async listPreviewComments(token, articleId) {
    const res = await apiClient.get(`${BASE}/previews/${token}/comments`, { params: { article_id: articleId } });
    return res.data; // { comments }
  },
  // Internal (AM dashboard).
  async listArticleComments(id) {
    const res = await apiClient.get(`${BASE}/articles/${id}/comments`);
    return res.data; // { comments, total }
  },
  async updateComment(id, status) {
    const res = await apiClient.patch(`${BASE}/comments/${id}`, { status });
    return res.data;
  },
  async deleteComment(id) {
    const res = await apiClient.delete(`${BASE}/comments/${id}`);
    return res.data;
  },

  // ── Distribution ──────────────────────────────────────────────────────────
  async listDestinations() {
    const res = await apiClient.get(`${BASE}/destinations`);
    return res.data; // { destinations, total }
  },
  // Placements — post-push per-outlet tracking (Distribution board).
  async listPlacements(params = {}) {
    const res = await apiClient.get(`${BASE}/placements`, { params });
    return res.data; // { items, total, skip, limit }
  },
  async placementStats() {
    const res = await apiClient.get(`${BASE}/placements/stats`);
    return res.data; // { total, by_status, by_channel, awaiting_link, not_yet_live }
  },
  // Move a placement to a different publication. Also rewrites the article's destination ledger
  // server-side — without that the next re-materialization would restore the old outlet.
  // Refuses (409) on an already-pushed Win, a duplicate outlet, or PRNow/Journal placements.
  async changePlacementOutlet(id, outletName) {
    const res = await apiClient.post(`${BASE}/placements/${id}/change-outlet`, { outlet_name: outletName });
    return res.data; // the updated placement
  },
  async updatePlacement(id, data) {
    // data: { status?, url?, note?, source? } — link-overwrite protection enforced server-side.
    const res = await apiClient.patch(`${BASE}/placements/${id}`, data);
    return res.data;
  },
  async getArticle(id) {
    const res = await apiClient.get(`${BASE}/articles/${id}`);
    return res.data; // the article document
  },
  // Append one dated note to an article. `placementId` null → the note is about the piece and
  // shows against every outlet; set → it belongs to that one outlet's handling of it.
  // Append-only server-side: there is deliberately no edit/delete counterpart to call.
  async addArticleNote(articleId, text, placementId = null) {
    const res = await apiClient.post(`${BASE}/articles/${articleId}/notes`, {
      text, placement_id: placementId,
    });
    return res.data; // the created note
  },
  // Win-Finder — proposes a candidate live URL (writes placement.suggested_url).
  // Never marks live / never a Win; a human Save does that.
  async findPlacementUrl(id) {
    // Can take a while: domain resolution + the browser engine solving Cloudflare
    // + the Claude web-search engine. Backend engines have their own deadlines;
    // this ceiling just has to outlast their worst-case sum.
    const res = await apiClient.post(`${BASE}/placements/${id}/find-url`, null, { timeout: 180000 });
    return res.data; // { result: { outcome, reason, ... }, placement }
  },
  async findPlacementUrlsBulk(lens = 'submitted_waiting') {
    const res = await apiClient.post(`${BASE}/placements/find-urls`, null, { params: { lens } });
    return res.data; // { message, lens } | { message, status }
  },
  async winFinderStatus() {
    const res = await apiClient.get(`${BASE}/placements/find-urls/status`);
    return res.data; // { state, total, processed, found, ... }
  },
  async pushWin(id) {
    // Push a live placement to the KC-OS Wins table (idempotent server-side).
    const res = await apiClient.post(`${BASE}/placements/${id}/push-win`);
    return res.data; // refreshed placement (win.pushed / win.error)
  },
  async listCategories() {
    const res = await apiClient.get(`${BASE}/categories`);
    return res.data; // { categories, total }
  },

  // ── Shareable previews ────────────────────────────────────────────────────
  async createPreview(articleIds, title = '') {
    const res = await apiClient.post(`${BASE}/previews`, { article_ids: articleIds, title });
    return res.data; // { token, ... }
  },
  // Compose the "Your {Month} Content Is Ready" client email for these articles.
  // previewUrls maps article_id → its own shareable preview URL.
  async buildPreviewEmail(articleIds, previewUrls, senderName = '') {
    const res = await apiClient.post(`${BASE}/preview-email`, { article_ids: articleIds, preview_urls: previewUrls, sender_name: senderName });
    return res.data; // { to, subject, body_html, body_text, missing_email, ... }
  },
  async getPreview(token) {
    const res = await apiClient.get(`${BASE}/previews/${token}`);
    return res.data; // { token, title, articles: [...] }
  },

  // ── Settings — editable WordPress registries ──────────────────────────────
  async listRegistries() {
    const res = await apiClient.get(`${BASE}/settings/registries`);
    return res.data; // { destinations, categories }
  },
  async createDestination(data) {
    const res = await apiClient.post(`${BASE}/settings/destinations`, data);
    return res.data;
  },
  async updateDestination(id, data) {
    const res = await apiClient.patch(`${BASE}/settings/destinations/${id}`, data);
    return res.data;
  },
  async deleteDestination(id) {
    const res = await apiClient.delete(`${BASE}/settings/destinations/${id}`);
    return res;
  },
  async createCategory(data) {
    const res = await apiClient.post(`${BASE}/settings/categories`, data);
    return res.data;
  },
  async updateCategory(id, data) {
    const res = await apiClient.patch(`${BASE}/settings/categories/${id}`, data);
    return res.data;
  },
  async deleteCategory(id) {
    const res = await apiClient.delete(`${BASE}/settings/categories/${id}`);
    return res;
  },
  async syncWordPressRegistries() {
    const res = await apiClient.post(`${BASE}/settings/sync-wordpress`, {});
    return res.data;
  },
  async wordpressStatus() {
    const res = await apiClient.get(`${BASE}/settings/wordpress-status`);
    return res.data; // { connected, base_url }
  },
  async uploadArticleImage(id, file) {
    const form = new FormData();
    form.append('file', file);
    const res = await apiClient.post(`${BASE}/articles/${id}/image`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  },
  // ── Additional images (force a manual-submission task when present) ───────
  async uploadAdditionalImage(id, file) {
    const form = new FormData();
    form.append('file', file);
    const res = await apiClient.post(`${BASE}/articles/${id}/additional-images`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  },
  async addAdditionalImageFromLibrary(id, { storage_path, filename }) {
    const res = await apiClient.post(`${BASE}/articles/${id}/additional-images/library`, { storage_path, filename });
    return res.data;
  },
  async updateAdditionalImage(id, { storage_path, caption, credit }) {
    const res = await apiClient.patch(`${BASE}/articles/${id}/additional-images`, { storage_path, caption, credit });
    return res.data;
  },
  async removeAdditionalImage(id, storagePath) {
    const res = await apiClient.delete(`${BASE}/articles/${id}/additional-images`, { params: { storage_path: storagePath } });
    return res.data;
  },
  // ── PRNow press distribution (read-only catalog + dry-run preview; never sends) ──
  async getPrnowPackages() {
    const res = await apiClient.get(`${BASE}/prnow/packages`);
    return res.data; // { configured, balance, plans, sending_locked }
  },
  async previewPrnow(id, planIds) {
    const res = await apiClient.post(`${BASE}/articles/${id}/prnow/preview`, { plan_ids: planIds });
    return res.data; // { selected, total_credits, balance, affordable, validation, sending_locked }
  },
  async commitPrnowManual(id) {
    // Creates the manual upload task for staged PRNow plans (no API call, no credits).
    const res = await apiClient.post(`${BASE}/articles/${id}/prnow/commit-manual`, {});
    return res.data;
  },
  async updatePrnowPlan(id, { plan_id, status, url }) {
    const res = await apiClient.post(`${BASE}/articles/${id}/prnow/plan-status`, { plan_id, status, url });
    return res.data;
  },
  // Permanently delete ONE manual task (an external outlet by name, or a PRNow plan
  // by plan_id). Account-manager action — callers gate it behind a confirmation.
  async deleteManualTask(id, { tier, name, plan_id }) {
    const res = await apiClient.post(`${BASE}/articles/${id}/manual-task/delete`, { tier, name, plan_id });
    return res.data;
  },
  // Sidebar badge counts: { articles, manual }.
  async getNavCounts() {
    const res = await apiClient.get(`${BASE}/nav-counts`);
    return res.data;
  },
  async pushToWordPress(id) {
    // Image upload + WordPress thumbnail generation can outlive the global 30s
    // timeout — give the push the same headroom as the backend's WP client.
    const res = await apiClient.post(`${BASE}/articles/${id}/push-wordpress`, {}, { timeout: 200000 });
    return res.data; // { article, wordpress }
  },
  /**
   * Pull each pushed article's current WordPress state onto its record. Read-only against WordPress,
   * additive here (only `wp_*` mirror fields). Covers both pipelines in one run.
   * Generous timeout: the run fetches status labels, three taxonomies and the user list, then one
   * batch per 100 articles.
   */
  async syncWordPressStatus() {
    const res = await apiClient.post(`${BASE}/articles/sync-wordpress-status`, {}, { timeout: 120000 });
    return res.data;
  },
  async updateExternalDestination(id, payload) {
    const res = await apiClient.post(`${BASE}/articles/${id}/destinations/external`, payload);
    return res.data;
  },
  async factCheckArticle(id) {
    const res = await apiClient.post(`${BASE}/articles/${id}/fact-check`, {});
    return res.data; // { fact_check, verdict, article }
  },
  // Shutterstock methods removed 2026-08-19 — see the note in articleService.js.

  // ── Sources & partners ────────────────────────────────────────────────────
  // The SAME `sources` registry the editorial side uses — there is no separate Content Studio
  // person. Someone appears here only by belonging to a partner. Partner detail comes from the
  // local mirror, never a live KeyCrew OS call.
  /** The article's stored location, ids resolved to names. Read-only. */
  async getGeo(id) {
    return apiClient.get(`${BASE}/articles/${id}/geo`);
  },

  /** Propose a location. Never overwrites one a human has set — the backend refuses. */
  /** Save a hand-edited location. Stamps it as a human decision. */
  async setGeo(id, geo) {
    return apiClient.put(`${BASE}/articles/${id}/geo`, geo);
  },

  async suggestGeo(id) {
    return apiClient.post(`${BASE}/articles/${id}/suggest-geo`);
  },

  async listSources(filters = {}) {
    const params = Object.fromEntries(
      Object.entries(filters).filter(([, v]) => v !== undefined && v !== null && v !== '')
    );
    const res = await apiClient.get(`${BASE}/sources`, { params });
    return res.data; // { items, total, skip, limit }
  },
  async listPartners(activeOnly = false) {
    const res = await apiClient.get(`${BASE}/partners`, { params: { active_only: activeOnly } });
    return res.data; // { items: [{id,name,status,engagement_type,member_count,...}], total }
  },
};

export default contentStudioService;
